import { Link } from 'react-router-dom';
import {
  ArrowRight, Shield, LineChart, Microscope, Building2,
  Zap, Search, Database, CheckCircle, Globe, Users,
  Lightbulb, Code, BarChart3, Rocket, ShieldCheck, BarChart2, Cpu
} from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';
import heroImg from '../img/Hero-Section-Image.png';
import user1 from '../img/user1.jpg';
import user2 from '../img/user2.jpg';
import user3 from '../img/user3.jpg';

const SectionTitle = ({ eyebrow, title, subtitle, centered = false }) => (
  <div className={`max-w-3xl mb-12 ${centered ? 'mx-auto text-center' : ''}`}>
    {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#005a9c] mb-3">{eyebrow}</p>}
    <h2 className="text-3xl md:text-[2.25rem] font-bold text-[#2c2c2c] tracking-tight leading-tight">{title}</h2>
    {subtitle && <p className="mt-4 text-[#5c5c5c] text-[1.05rem] leading-relaxed">{subtitle}</p>}
  </div>
);

export default function Home() {
  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="rf-hero-band border-b border-[#e5eef6] pt-12 pb-20 md:py-24">
        <RfPageFrame className="!py-0">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#005a9c]/5 border border-[#005a9c]/10 text-[#005a9c] text-xs font-bold tracking-wider uppercase">
                <Rocket className="w-3 h-3" />
                End-to-End AI Solutions
              </div>
              <h1 className="text-4xl md:text-[3.5rem] font-bold text-[#2c2c2c] leading-[1.1] tracking-tight">
                Empowering your <span className="text-[#005a9c]">AI journey</span> from vision to production
              </h1>
              <p className="text-[1.15rem] text-[#5c5c5c] leading-relaxed max-w-xl">
                RakFort is your partner for full-spectrum AI transformation. We design, build, and secure 
                high-impact AI capabilities that drive enterprise growth and operational excellence.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/services"
                  className="px-8 py-4 bg-[#005a9c] text-white font-bold rounded-lg hover:bg-[#004a82] transition-all shadow-lg shadow-[#005a9c]/20 flex items-center gap-2"
                >
                  Explore Services
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/solutions/ai-security"
                  className="px-8 py-4 border-2 border-[#005a9c] text-[#005a9c] font-bold rounded-lg hover:bg-[#005a9c]/5 transition-all"
                >
                  Our Solutions
                </Link>
              </div>
              <div className="pt-4 flex items-center gap-6 opacity-60 grayscale">
                <div className="flex items-center gap-2 font-bold text-sm tracking-tighter uppercase">Enterprise Ready</div>
                <div className="h-px flex-1 bg-slate-200"></div>
              </div>
            </div>
            <div className="lg:w-1/2 relative">
              <div className="absolute -inset-4 bg-[#005a9c]/5 rounded-3xl blur-3xl"></div>
              <div className="relative rf-card p-2 rounded-3xl shadow-2xl border-[#e5eef6]">
                <img 
                  src={heroImg} 
                  alt="RakFort AI Ecosystem" 
                  className="rounded-2xl w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      {/* Stats Section — Balanced impact */}
      <section className="py-20 border-b border-[#e5eef6] bg-[#f0f7ff]">
        <RfPageFrame>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { label: 'AI Projects Delivered', value: '50+' },
              { label: 'Security Threats Blocked', value: '1M+' },
              { label: 'Customer ROI Increase', value: '40%' },
              { label: 'Governance Readiness', value: '100%' },
            ].map((stat) => (
              <div key={stat.label} className="text-center space-y-2">
                <div className="text-4xl md:text-5xl font-extrabold text-[#005a9c] tracking-tighter">{stat.value}</div>
                <div className="text-sm font-semibold text-[#5c5c5c] uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </RfPageFrame>
      </section>

      {/* Core Service Lines — The "Builder" side */}
      <section className="py-24 border-b border-[#e5eef6]">
        <RfPageFrame>
          <SectionTitle 
            centered
            eyebrow="Expertise"
            title="Strategic AI Services"
            subtitle="We help organizations navigate the complexities of AI, from initial strategy and talent readiness to custom implementation and lifecycle management."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'AI Consulting',
                desc: 'Strategic advisory on use-case identification and ethical AI adoption.',
                icon: Lightbulb,
                link: '/services/ai-consulting',
                color: 'text-yellow-600'
              },
              {
                title: 'Human Trust Services',
                desc: 'AI-era human risk management: SimStrike attack simulations and the AI Trust & Readiness Programme.',
                icon: ShieldCheck,
                link: '/services/human-trust',
                color: 'text-emerald-600'
              },
              {
                title: 'Human Resources',
                desc: 'Preparing your teams and culture for an AI-enabled future.',
                icon: Users,
                link: '/services/human-resources',
                color: 'text-orange-600'
              },
              {
                title: 'Risk Management',
                desc: 'Enterprise risk framing for digital and AI-driven initiatives.',
                icon: BarChart3,
                link: '/services/risk-management',
                color: 'text-purple-600'
              }
            ].map((service) => (
              <Link 
                key={service.title} 
                to={service.link}
                className="rf-card p-8 group hover:border-[#005a9c]/30 transition-all flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mb-6 border border-slate-100 group-hover:bg-white group-hover:shadow-sm transition-all">
                  <service.icon className={`w-6 h-6 ${service.color}`} />
                </div>
                <h3 className="text-xl font-bold text-[#2c2c2c] mb-3 group-hover:text-[#005a9c] transition-colors">{service.title}</h3>
                <p className="text-[#5c5c5c] text-sm leading-relaxed mb-6">{service.desc}</p>
                <div className="mt-auto flex items-center text-sm font-bold text-[#005a9c]">
                  Open Service <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </RfPageFrame>
      </section>

      {/* Platform & Security */}
      <section className="py-24 border-b border-[#e5eef6] bg-[#f0f7ff]">
        <RfPageFrame>
          <SectionTitle
            centered
            eyebrow="Platform"
            title="AI Governance, Security & Human Trust"
            subtitle="Professional-grade tools and solutions to ensure your AI systems are safe, compliant, and resilient — and your people are ready."
          />

          {/* AI Security products */}
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#5c6d7e] mb-4">AI Security</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                title: 'ScanFort',
                desc: 'Automated penetration testing and red teaming for GenAI models.',
                icon: Search,
                link: '/scanfort',
                color: 'text-blue-600'
              },
              {
                title: 'GuardFort',
                desc: 'Real-time monitoring and firewall protection for AI interfaces.',
                icon: Shield,
                link: '/guardfort',
                color: 'text-green-600'
              },
              {
                title: 'ModelFort',
                desc: 'Deep analysis of model weights and artifacts for integrity.',
                icon: Database,
                link: '/modelfort',
                color: 'text-purple-600'
              },
              {
                title: 'RiskFort',
                desc: 'Structured AI risk registers and governance scoring.',
                icon: LineChart,
                link: '/riskfort',
                color: 'text-orange-600'
              },
              {
                title: 'AiGov',
                desc: 'Enterprise readiness platform for regulatory compliance.',
                icon: CheckCircle,
                link: '/aigov',
                color: 'text-[#005a9c]'
              },
              {
                title: 'Global Research',
                desc: 'Applied research into the next generation of AI safety.',
                icon: Microscope,
                link: '/research',
                color: 'text-red-600'
              }
            ].map((product) => (
              <Link
                key={product.title}
                to={product.link}
                className="rf-card p-8 group hover:border-[#005a9c]/30 transition-all flex flex-col bg-white"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mb-6 border border-slate-100">
                  <product.icon className={`w-6 h-6 ${product.color}`} />
                </div>
                <h3 className="text-xl font-bold text-[#2c2c2c] mb-3 group-hover:text-[#005a9c] transition-colors">{product.title}</h3>
                <p className="text-[#5c5c5c] text-sm leading-relaxed flex-1 mb-6">{product.desc}</p>
                <div className="flex items-center text-sm font-bold text-[#005a9c]">
                  View Product <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          {/* Human Trust solutions */}
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#5c6d7e] mb-4">Human Trust</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'TrustScore',
                desc: 'Quantified human risk assessment across five AI-era dimensions — with a baseline, remediation, and board-ready re-assessment report.',
                icon: BarChart2,
                link: '/solutions/trustscore',
                bg: 'bg-[#E1F5EE]',
                color: 'text-[#0F6E56]'
              },
              {
                title: 'DeepStrike',
                desc: 'Managed multi-vector AI attack simulation campaigns — prompt injection, deepfake vishing, synthetic identity probes. Fully managed, quarterly cadence.',
                icon: Zap,
                link: '/solutions/deepstrike',
                bg: 'bg-[#FAECE7]',
                color: 'text-[#993C1D]'
              },
              {
                title: 'AgentReady',
                desc: 'Human readiness for enterprise AI agent rollouts across any platform — pre-deployment training, policies, TrustScore baseline, and ongoing simulation.',
                icon: Cpu,
                link: '/solutions/copilotready',
                bg: 'bg-[#E6F1FB]',
                color: 'text-[#185FA5]'
              }
            ].map((product) => (
              <Link
                key={product.title}
                to={product.link}
                className="rf-card p-8 group hover:border-[#005a9c]/30 transition-all flex flex-col bg-white"
              >
                <div className={`w-12 h-12 rounded-xl ${product.bg} flex items-center justify-center mb-6`}>
                  <product.icon className={`w-6 h-6 ${product.color}`} />
                </div>
                <h3 className="text-xl font-bold text-[#2c2c2c] mb-3 group-hover:text-[#005a9c] transition-colors">{product.title}</h3>
                <p className="text-[#5c5c5c] text-sm leading-relaxed flex-1 mb-6">{product.desc}</p>
                <div className="flex items-center text-sm font-bold text-[#005a9c]">
                  View Solution <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </RfPageFrame>
      </section>

      {/* Value Pillars — Strategy + Engineering */}
      <section className="py-24 bg-transparent border-b border-[#e5eef6]">
        <RfPageFrame>
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2">
              <SectionTitle 
                eyebrow="Our Value"
                title="Engineering-led, Strategy-driven"
                subtitle="We bridge the gap between abstract AI strategy and complex technical execution, ensuring every AI initiative delivers measurable value."
              />
              <div className="space-y-8">
                {[
                  {
                    title: 'Speed to Value',
                    text: 'Move from pilot to production rapidly with our pre-built frameworks and automated tools.',
                    icon: Zap
                  },
                  {
                    title: 'Future-Proof Governance',
                    text: 'Comply with global regulations like the EU AI Act while maintaining innovation agility.',
                    icon: Globe
                  },
                  {
                    title: 'Cross-Domain Expertise',
                    text: 'A global team combining AI research, cybersecurity, and enterprise product engineering.',
                    icon: Building2
                  }
                ].map((item) => (
                  <div key={item.title} className="flex gap-5">
                    <div className="w-12 h-12 rounded-full bg-[#005a9c]/10 flex items-center justify-center shrink-0 border border-[#005a9c]/20">
                      <item.icon className="w-5 h-5 text-[#005a9c]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#2c2c2c] mb-1">{item.title}</h4>
                      <p className="text-sm text-[#5c5c5c] leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="rf-card p-10 bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Rocket className="w-48 h-48" />
                </div>
                <h3 className="text-2xl font-bold mb-6">AI Readiness Pulse</h3>
                <div className="space-y-6 relative z-10">
                  {[
                    { label: 'Strategic Alignment', value: 92 },
                    { label: 'Technical Implementation', value: 85 },
                    { label: 'Governance & Security', value: 98 }
                  ].map((bar) => (
                    <div key={bar.label}>
                      <div className="flex justify-between text-xs font-bold uppercase tracking-widest mb-2 text-slate-400">
                        <span>{bar.label}</span>
                        <span>{bar.value}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-[#005a9c] rounded-full" style={{ width: `${bar.value}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-10 pt-10 border-t border-slate-800 grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-2xl font-bold text-white">Full-Stack</div>
                    <div className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">Capabilities</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#005a9c]">Certified</div>
                    <div className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">Frameworks</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      {/* Community & Innovation Section */}
      <section className="py-24 border-b border-[#e5eef6] bg-[#f0f7ff]">
        <RfPageFrame>
          <SectionTitle 
            centered
            eyebrow="Ecosystem"
            title="Advancing the field of AI Security"
            subtitle="We believe in a collaborative approach to AI safety. Our team actively contributes to the global community through research, open-source tools, and practitioner hubs."
          />
          <div className="grid md:grid-cols-3 gap-8">
            <div className="rf-card p-10 flex flex-col hover:border-[#005a9c]/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-8 border border-blue-100 group-hover:bg-[#005a9c] group-hover:text-white transition-all">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-[#2c2c2c] mb-4">AISecops Community</h3>
              <p className="text-[#5c5c5c] text-sm leading-relaxed mb-8 flex-1">
                A collaborative hub for AI security practitioners, researchers, and engineers to share patterns, 
                defensive strategies, and emerging threat intelligence.
              </p>
              <a href="https://www.linkedin.com/company/aisecops/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#005a9c] font-bold text-sm hover:underline">
                Join the community <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="rf-card p-10 flex flex-col hover:border-[#005a9c]/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center mb-8 border border-purple-100 group-hover:bg-[#005a9c] group-hover:text-white transition-all">
                <Microscope className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-[#2c2c2c] mb-4">AI Security Research</h3>
              <p className="text-[#5c5c5c] text-sm leading-relaxed mb-8 flex-1">
                Leading-edge research into adversarial machine learning, prompt injection vulnerabilities, 
                and the development of robust defensive frameworks.
              </p>
              <Link to="/research" className="inline-flex items-center gap-2 text-[#005a9c] font-bold text-sm hover:underline">
                View publications <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="rf-card p-10 flex flex-col hover:border-[#005a9c]/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-8 border border-orange-100 group-hover:bg-[#005a9c] group-hover:text-white transition-all">
                <Code className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-[#2c2c2c] mb-4">Open Source Tools</h3>
              <p className="text-[#5c5c5c] text-sm leading-relaxed mb-8 flex-1">
                We contribute back to the security community with open-source utilities for scanning, 
                testing, and hardening AI models and deployment pipelines.
              </p>
              <a href="https://github.com/rakfortltd" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#005a9c] font-bold text-sm hover:underline">
                Browse on GitHub <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </RfPageFrame>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-b border-[#e5eef6]">
        <RfPageFrame>
          <SectionTitle 
            centered
            eyebrow="Testimonials"
            title="Trusted by innovators worldwide"
          />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Chen',
                role: 'CTO, FinTech Global',
                img: user1,
                quote: 'RakFort provided the strategic roadmap and technical muscle we needed to deploy our first LLM-powered customer assistant safely.'
              },
              {
                name: 'David Miller',
                role: 'Head of Innovation, HealthTech',
                img: user2,
                quote: 'The combination of their implementation services and governance platform is unique. They build it right, and they keep it safe.'
              },
              {
                name: 'James Wilson',
                role: 'VP Engineering, Enterprise AI',
                img: user3,
                quote: 'Finally a partner that understands both the creative potential of generative AI and the rigorous requirements of enterprise security.'
              }
            ].map((t) => (
              <div key={t.name} className="rf-card p-8 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full overflow-hidden mb-6 border-2 border-[#005a9c]/20">
                  <img src={t.img} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <p className="italic text-[#5c5c5c] mb-6 leading-relaxed">"{t.quote}"</p>
                <div>
                  <div className="font-bold text-[#2c2c2c]">{t.name}</div>
                  <div className="text-xs font-semibold text-[#005a9c] uppercase tracking-wider">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </RfPageFrame>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#005a9c] text-white">
        <RfPageFrame className="text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8 leading-tight">Build the AI-powered future with RakFort</h2>
          <p className="text-blue-100 text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
            Join the organizations driving real business impact through trustworthy, scalable, and secure AI systems. 
            Talk to our team about your next move.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contactus"
              className="px-10 py-4 bg-white text-[#005a9c] font-bold rounded-lg hover:bg-slate-50 transition-all shadow-xl"
            >
              Start Your Transformation
            </Link>
            <Link
              to="/services"
              className="px-10 py-4 border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition-all"
            >
              Our Service Portfolio
            </Link>
          </div>
        </RfPageFrame>
      </section>
    </div>
  );
}
